import { useRef } from 'react';
import { ImageIcon, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Callout } from '../../components/ui/Callout';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { extractErrorMessage } from '../../api/errors';
import { useBrandAssets, useUploadAsset, type AssetSlot } from '../../queries/brandVoice';

const DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

interface ImagesPanelProps {
  canWrite: boolean;
  brandName: string;
}

/**
 * Reference images attached to every social image generation. An upload is
 * stored under a new name and the slot switches to it, so images already
 * posted keep pointing at the version they were made with.
 */
export function ImagesPanel({ canWrite, brandName }: ImagesPanelProps) {
  const { data, isLoading, error } = useBrandAssets();
  const upload = useUploadAsset();
  const toast = useToast();

  if (isLoading) return <Spinner />;
  if (error || !data) return <Callout variant="error" title="Could not load brand images">{extractErrorMessage(error, '')}</Callout>;

  const send = (slot: AssetSlot, file: File) => {
    // The server checks again (including the file's actual bytes); this just
    // saves a round trip for the obvious mistakes.
    if (!data.allowedTypes.includes(file.type)) {
      toast.error(`${file.name} is not a PNG, JPEG or WebP image.`);
      return;
    }
    if (file.size > data.maxBytes) {
      toast.error(`${file.name} is larger than ${Math.round(data.maxBytes / 1024 / 1024)} MB.`);
      return;
    }
    upload.mutate(
      { key: slot.key, file },
      {
        onSuccess: () => toast.success(`${slot.label} updated. Workflows use it from their next run.`),
        onError: (err) => toast.error(extractErrorMessage(err, `Failed to upload ${slot.label}.`)),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4" /> Reference images
        </CardTitle>
        <CardDescription>
          Attached to every {brandName} social image generation so the mascot, style and logo come out right. Uploading replaces the image
          for future posts only.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.assets.map((slot) => (
            <AssetTile key={slot.key} slot={slot} canWrite={canWrite} busy={upload.isPending} accept={data.allowedTypes.join(',')} onFile={(f) => send(slot, f)} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function AssetTile({ slot, canWrite, busy, accept, onFile }: { slot: AssetSlot; canWrite: boolean; busy: boolean; accept: string; onFile: (file: File) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <li className="flex flex-col rounded-md border border-border p-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{slot.label}</span>
        {!slot.current && <Badge variant="warning">Not set</Badge>}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{slot.hint}</p>
      <div className="mt-3 flex aspect-square items-center justify-center overflow-hidden rounded bg-muted">
        {slot.current ? (
          <a href={slot.current.url} target="_blank" rel="noreferrer">
            <img src={slot.current.url} alt={slot.label} className="max-h-full max-w-full object-contain" />
          </a>
        ) : (
          <ImageIcon className="h-8 w-8 text-muted-foreground" aria-hidden />
        )}
      </div>
      {slot.current && (
        <p className="mt-2 text-xs text-muted-foreground">
          Updated {DATE_FORMATTER.format(new Date(slot.current.updated_at))}
          {slot.current.updated_by ? ` by ${slot.current.updated_by}` : ''}
        </p>
      )}
      {canWrite && (
        <>
          <input
            ref={input}
            type="file"
            accept={accept}
            className="sr-only"
            aria-label={`Upload ${slot.label}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) onFile(file);
            }}
          />
          <Button variant="outline" size="sm" className="mt-3" disabled={busy} onClick={() => input.current?.click()}>
            <Upload className="mr-1 h-3.5 w-3.5" /> {slot.current ? 'Replace' : 'Upload'}
          </Button>
        </>
      )}
    </li>
  );
}
